import * as go from 'gojs/release/go-module';

import { Layer } from 'src/containers/ProjectDiagram/constants/layers';
import {
  LINK_HEAD_NAME,
  LINK_TAIL_NAME,
} from 'src/containers/ProjectDiagram/constants/link';
import {
  DRAFT_OUTLINE_COLOR,
  LINK_ARROW_SCALE,
  LINK_COLOR,
  LINK_SELECTED_WIDTH,
  LINK_WIDTH,
} from 'src/containers/ProjectDiagram/constants/styles';
import {
  graphObjectMouseEnter,
  graphObjectMouseLeave,
} from 'src/containers/ProjectDiagram/handlers/graphObject';
import {
  headMouseEnter,
  headMouseLeave,
  headUnlinkClick,
  tailMouseEnter,
  tailMouseLeave,
  tailUnlinkClick,
} from 'src/containers/ProjectDiagram/handlers/modelConceptRelationship';
import { ExtendedLink } from 'src/containers/ProjectDiagram/templates/extensions/link';
import {
  dataBinding,
  modelBinding,
  objectBinding,
} from 'src/containers/ProjectDiagram/templates/helpers/binding';
import { isRelationshipModified } from 'src/containers/ProjectDiagram/templates/helpers/draft';
import { determineColorFromSelection } from 'src/containers/ProjectDiagram/templates/helpers/styles';
import { ModelConceptRelationship } from 'src/containers/ProjectDiagram/types/data';
import {
  Diagram,
  ModelConceptRelationshipLink,
} from 'src/containers/ProjectDiagram/types/diagram';
import { ModelData } from 'src/containers/ProjectDiagram/types/model';
import {
  isDetachableLinkHeadConverter,
  isDetachableLinkTailConverter,
} from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/isDetachableLinkEnd';

const determineRelationshipColor = determineColorFromSelection(
  isRelationshipModified,
  LINK_COLOR,
  DRAFT_OUTLINE_COLOR
);

const $ = go.GraphObject.make;

export const createModelConceptRelationshipTemplate = ({
  contextMenu,
  handleClick,
  handleDoubleClick,
}: {
  contextMenu: go.HTMLInfo;
  handleClick: NonNullable<ModelConceptRelationshipLink['click']>;
  handleDoubleClick: NonNullable<ModelConceptRelationshipLink['doubleClick']>;
}) =>
  $(
    ExtendedLink,
    {
      click: handleClick,
      contextMenu,
      cursor: 'pointer',
      curve: go.Link.Bezier,
      doubleClick: handleDoubleClick,
      layerName: Layer.MODEL_CONCEPT_RELATIONSHIP_BACKGROUND_LAYER,
      mouseEnter: graphObjectMouseEnter,
      mouseLeave: graphObjectMouseLeave,
      movable: false,
      selectionAdorned: false,
      toShortLength: LINK_ARROW_SCALE * LINK_WIDTH,
    },

    dataBinding<go.Link, ModelConceptRelationship>()('points').makeTwoWay(),
    dataBinding<go.Link, ModelConceptRelationship>()(
      'movable',
      '',
      (data) => !(data.from && data.to)
    ),
    dataBinding<go.Link, ModelConceptRelationship>()(
      'relinkableFrom',
      '',
      (data) => !data.from
    ),
    dataBinding<go.Link, ModelConceptRelationship>()(
      'relinkableTo',
      '',
      (data) => !data.to
    ),
    objectBinding<go.Link, go.Link>()('layerName', 'isSelected', (isSelected) =>
      isSelected
        ? Layer.MODEL_CONCEPT_RELATIONSHIP_FOREGROUND_LAYER
        : Layer.MODEL_CONCEPT_RELATIONSHIP_BACKGROUND_LAYER
    ),

    $(
      go.Shape, // the line
      {
        name: 'MAIN',
        isPanelMain: true,
        stroke: LINK_COLOR,
        strokeWidth: LINK_WIDTH,
      },
      modelBinding<go.Shape, ModelData>()(
        'stroke',
        '',
        determineRelationshipColor
      ),
      objectBinding<go.Shape, go.Link>()('strokeWidth', '', (link) =>
        link.isHighlighted || link.isSelected ? LINK_SELECTED_WIDTH : LINK_WIDTH
      )
    ),

    /**
     * NOTE: The link "head" comprises two distinct shapes: (1) the shape for
     * the link head's "border" (which shows _only_ when the link is able to be
     * disconnected) and (2) the shape for the "internal" (non-border) part of
     * the head (which is a standard arrow shape except in the case where the
     * link is highlighted and disconnectable, in which case it is a "unlink"
     * shape. Things are done this way so that, when the "unlink" shape is
     * visible, its interior paths can be filled black, and the remaining space
     * around the shape (but still within the border) can be filled white, so
     * that the link's path/"stem" does not show directly beneath the "unlink"
     * shape (which looks ugly).
     */
    $(
      go.Shape, // the link head's border, only visible when link is highlighted and disconnectable
      {
        click: headUnlinkClick,
        fill: 'transparent',
        isActionable: true,
        mouseEnter: headMouseEnter,
        mouseLeave: headMouseLeave,
        name: `${LINK_HEAD_NAME}_BORDER`,
        scale: LINK_ARROW_SCALE * 1.75,
        stroke: 'transparent',
        strokeWidth: 0.5,
        toArrow: 'Circle',
      },
      // This shape is always transparent except in the case where the head is
      // "detachable".
      modelBinding<go.Shape, ModelData>()(
        'fill',
        'highlighted',
        (highlighted, shape) =>
          isDetachableLinkHeadConverter.forModelHighlighted(highlighted, shape)
            ? 'white'
            : 'transparent'
      ),
      modelBinding<go.Shape, ModelData>()('stroke', '', (modelData, shape) =>
        isDetachableLinkHeadConverter.forModelHighlighted(
          modelData.highlighted,
          shape
        )
          ? determineRelationshipColor(modelData, shape)
          : 'transparent'
      ),
      objectBinding<go.Shape, go.Link>()('fill', 'toNode', (toNode, shape) =>
        // This binding ensures that the 'Unlink' icon's circle is hidden
        // when the link is disconnected, even if it is still highlighted.
        isDetachableLinkHeadConverter.forObjectEndNode(toNode, shape)
          ? 'white'
          : 'transparent'
      ),
      objectBinding<go.Shape, go.Link>()('stroke', 'toNode', (toNode, shape) =>
        // This binding ensures that the 'Unlink' icon's circle is hidden
        // when the link is disconnected, even if it is still highlighted.
        isDetachableLinkHeadConverter.forObjectEndNode(toNode, shape)
          ? determineRelationshipColor(
              (shape.part?.diagram as Diagram).model.modelData,
              shape
            )
          : 'transparent'
      ),
      objectBinding<go.Shape, go.Link>()('isActionable', 'toNode', Boolean)
    ),
    $(
      go.Shape, // the link head
      {
        click: headUnlinkClick,
        fill: LINK_COLOR,
        isActionable: true,
        mouseEnter: headMouseEnter,
        mouseLeave: headMouseLeave,
        name: LINK_HEAD_NAME,
        scale: LINK_ARROW_SCALE,
        stroke: LINK_COLOR,
        strokeWidth: 0.1, // this is required for the Unlink arrow icon not to become a mush when scaled
        toArrow: 'Triangle',
      },
      modelBinding<go.Shape, ModelData>()(
        'toArrow',
        'highlighted',
        (highlighted, shape) =>
          isDetachableLinkHeadConverter.forModelHighlighted(highlighted, shape)
            ? 'Unlink'
            : 'Triangle'
      ),
      modelBinding<go.Shape, ModelData>()(
        'stroke',
        '',
        determineRelationshipColor
      ),
      modelBinding<go.Shape, ModelData>()(
        'fill',
        '',
        determineRelationshipColor
      ),
      objectBinding<go.Shape, go.Link>()('scale', '', (link) =>
        link.isHighlighted || link.isSelected
          ? LINK_ARROW_SCALE * 1.5
          : LINK_ARROW_SCALE
      ),
      objectBinding<go.Shape, go.Link>()('toArrow', 'toNode', (toNode, shape) =>
        isDetachableLinkHeadConverter.forObjectEndNode(toNode, shape)
          ? 'Unlink'
          : 'Triangle'
      ),
      objectBinding<go.Shape, go.Link>()('isActionable', 'toNode', Boolean)
    ),

    /**
     * NOTE: The link "tail" comprises two distinct shapes: (1) the shape for
     * the link tail's "border" and (2) the shape for the "internal" (non-border) part of
     * the tail (which is a standard arrow shape except in the case where the
     * link is highlighted and disconnectable, in which case it is a "unlink"
     * shape. Things are done this way so that, when the "unlink" shape is
     * visible, its interior paths can be filled black, and the remaining space
     * around the shape (but still within the border) can be filled white, so
     * that the link's path/"stem" does not show directly beneath the "unlink"
     * shape (which looks ugly).
     */
    $(
      go.Shape, // the link tail's border, only visible when link is highlighted and disconnectable
      {
        click: tailUnlinkClick,
        fill: 'transparent',
        isActionable: true,
        mouseEnter: tailMouseEnter,
        mouseLeave: tailMouseLeave,
        name: `${LINK_TAIL_NAME}_BORDER`,
        scale: LINK_ARROW_SCALE * 1.75,
        stroke: 'transparent',
        strokeWidth: 0.5,
        fromArrow: 'Circle',
      },
      modelBinding<go.Shape, ModelData>()(
        'fill',
        'highlighted',
        (highlighted, shape) =>
          // When this shape is visible, it needs to be filled white so that
          // arrow path (or "stem") is not visible below it.
          isDetachableLinkTailConverter.forModelHighlighted(highlighted, shape)
            ? 'white'
            : 'transparent'
      ),
      modelBinding<go.Shape, ModelData>()('stroke', '', (modelData, shape) =>
        isDetachableLinkTailConverter.forModelHighlighted(
          modelData.highlighted,
          shape
        )
          ? determineRelationshipColor(modelData, shape)
          : 'transparent'
      ),
      objectBinding<go.Shape, go.Link>()(
        'fill',
        'fromNode',
        (fromNode, shape) =>
          // This binding ensures that the 'Unlink' icon's circle is hidden
          // when the link is disconnected, even if it is still highlighted.
          isDetachableLinkTailConverter.forObjectEndNode(fromNode, shape)
            ? 'white'
            : 'transparent'
      ),
      objectBinding<go.Shape, go.Link>()(
        'stroke',
        'fromNode',
        (fromNode, shape) =>
          // This binding ensures that the 'Unlink' icon's circle is hidden
          // when the link is disconnected, even if it is still highlighted.
          isDetachableLinkTailConverter.forObjectEndNode(fromNode, shape)
            ? determineRelationshipColor(
                (shape.part?.diagram as Diagram).model.modelData,
                shape
              )
            : 'transparent'
      ),
      objectBinding<go.Shape, go.Link>()('isActionable', 'fromNode', Boolean)
    ),
    $(
      go.Shape, // the link tail, only visible when link is highlighted and disconnectable
      {
        click: tailUnlinkClick,
        fill: 'transparent',
        fromArrow: 'Unlink',
        isActionable: true,
        mouseEnter: tailMouseEnter,
        mouseLeave: tailMouseLeave,
        name: LINK_TAIL_NAME,
        scale: LINK_ARROW_SCALE * 1.5,
        stroke: 'transparent',
        strokeWidth: 0.1, // this is required for the Unlink arrow icon not to become a mush when scaled
      },
      modelBinding<go.Shape, ModelData>()('stroke', '', (modelData, shape) =>
        isDetachableLinkTailConverter.forModelHighlighted(
          modelData.highlighted,
          shape
        )
          ? determineRelationshipColor(modelData, shape)
          : 'transparent'
      ),
      modelBinding<go.Shape, ModelData>()('fill', '', (modelData, shape) =>
        isDetachableLinkTailConverter.forModelHighlighted(
          modelData.highlighted,
          shape
        )
          ? determineRelationshipColor(modelData, shape)
          : 'transparent'
      ),
      objectBinding<go.Shape, go.Link>()(
        'fill',
        'fromNode',
        (fromNode, shape) =>
          // This binding ensures that the 'Unlink' icon is hidden when the
          // link is disconnected, even if it is still highlighted/selected.
          isDetachableLinkTailConverter.forObjectEndNode(fromNode, shape)
            ? determineRelationshipColor(
                (shape.part?.diagram as Diagram).model.modelData,
                shape
              )
            : 'transparent'
      ),
      objectBinding<go.Shape, go.Link>()(
        'stroke',
        'fromNode',
        (fromNode, shape) =>
          // This binding ensures that the 'Unlink' icon is hidden when the
          // link is disconnected, even if it is still highlighted/selected.
          isDetachableLinkTailConverter.forObjectEndNode(fromNode, shape)
            ? determineRelationshipColor(
                (shape.part?.diagram as Diagram).model.modelData,
                shape
              )
            : 'transparent'
      ),
      objectBinding<go.Shape, go.Link>()('isActionable', 'fromNode', Boolean)
    )
  );
